import { Outlet, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Steps } from '@/components/steps';
import { AppTitle } from '@/components/app-header/app-header';
import { createContext, useContext, useState } from 'react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { CampaignWizardProvider, useCampaignWizard } from './context/campaign-wizard-context';

interface StepContextType {
  setStepValid: (isValid: boolean) => void;
  isCurrentStepValid: boolean;
}

const StepContext = createContext<StepContextType | null>(null);

export function useStep() {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error('useStep must be used within a CampaignCreateLayout');
  }
  return context;
}

// Define steps as a regular array to match Steps component type
const steps = [
  {
    id: 'audience',
    label: 'Audience',
    description: 'Select the target audience segments for your campaign'
  },
  {
    id: 'design',
    label: 'Design',
    description: 'Create and customize your campaign message'
  },
  {
    id: 'review',
    label: 'Review',
    description: 'Review campaign details and settings before launching'
  }
];

function CampaignCreateLayoutInner() {
  const { name, currentStep, loading, handleNext, handleBack } = useCampaignWizard();
  const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
  const currentStepIndex = steps.findIndex((step) => step.id === currentStep);

  // Create steps with dynamic disabled state
  const stepsWithState = steps.map((step, index) => ({
    ...step,
    disabled: index > currentStepIndex + (isCurrentStepValid ? 1 : 0)
  }));

  return (
    <StepContext.Provider
      value={{
        setStepValid: setIsCurrentStepValid,
        isCurrentStepValid
      }}
    >
      <div className="space-y-6">
        <AppTitle>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink className="text-base" asChild>
                  <Link to="..">Campaigns</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-base">{name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </AppTitle>

        <div className="mx-auto max-w-8xl">
          <Steps
            steps={stepsWithState}
            currentStep={currentStep}
            onStepClick={() => {
              handleNext();
            }}
          />

          <div className="mt-8">
            <Outlet />
          </div>

          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={handleBack}>
              {currentStepIndex === 0 ? 'Cancel' : 'Previous'}
            </Button>

            <Button onClick={handleNext} disabled={!isCurrentStepValid || loading}>
              {loading
                ? 'Saving...'
                : currentStepIndex === steps.length - 1
                  ? 'Create Campaign'
                  : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </StepContext.Provider>
  );
}

export function CampaignCreateLayout() {
  return (
    <CampaignWizardProvider>
      <CampaignCreateLayoutInner />
    </CampaignWizardProvider>
  );
}
